import { Directive, Component, ChangeDetectionStrategy, input, contentChild, computed } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { DatePipe } from '@angular/common';
import { getSlotColor, RisrColor, RisrState } from '../utils';

@Directive({ selector: '[risrSlotCardTitle]' })
export class SlotCardTitle {}

@Directive({ selector: '[risrSlotCardState]' })
export class SlotCardState {}

@Directive({ selector: '[risrSlotCardMeta]' })
export class SlotCardMeta {}

@Directive({ selector: '[risrSlotCardDate]' })
export class SlotCardDate {}

@Directive({ selector: '[risrSlotCardActions]' })
export class SlotCardActions {}

@Component({
  selector: 'risr-slot-card',
  imports: [RouterLink, MatButtonModule, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[attr.data-color]": "color()",
  },
  styles: [`
    :host {
      --surface: var(--color-white, #fff);
      --padding: calc(var(--base-padding, 0.64em) * 2);
      --space: var(--space-x, 0.64em);
      --radius: var(--border-radius, 4px);
      --card-shadow: var(--box-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
      --title-size: var(--text-2xl, 1.5rem);
      --color-state: var(--color-primary, #1f5b73);

      align-items: stretch;
      background-color: var(--surface);
      border-radius: var(--radius);
      box-shadow: var(--card-shadow);
      display: flex;
      gap: calc(var(--space) * 2);
      justify-content: space-between;
      padding: var(--padding);
      margin: var(--space) 0;
    }

    :host::before {
      background-color: var(--color-state);
      border-radius: var(--radius);
      content: '';
      display: block;
      flex-shrink: 0;
      width: 0.32em;
    }

    .slot-card__left {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      font-size: 1em;
      gap: var(--space);
      justify-content: center;
    }

    .slot-card__right {
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      text-align: end;
    }

    .slot-card__title {
      font-size: var(--title-size);
    }

    .slot-card__date {
      font-size: 1em;
      color: var(--color-gray);
    }

    .slot-card__actions {
      margin-bottom: auto;
      margin-top: auto;
    }

    :host[data-color="primary"] {
      --color-state: var(--color-primary-700);
    }

    :host[data-color="accent"] {
      --color-state: var(--color-info-700);
    }

    :host[data-color="warn"] {
      --color-state: var(--color-danger-700);
    }

    :host[data-color="neutral"] {
      --color-state: var(--color-gray-700);
    }

    ::ng-deep [risrSlotCardTitle] {
      font-size: var(--title-size);
    }

    ::ng-deep [risrSlotCardState] {
      color: var(--color-state);
    }

    ::ng-deep [risrSlotCardDate] {
      color: var(--color-gray);
    }

    ::ng-deep [risrSlotCardMeta] {
      display: flex;
      gap: var(--space);
    }

    ::ng-deep [risrSlotCardActions] {
      margin-bottom: auto;
      margin-top: auto;
    }

    /* Split buttons */
    ::ng-deep .split-buttons {
      align-items: stretch;
      display: inline-flex;
      gap: 0.4em;
    }

    ::ng-deep .split-buttons__main-button {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }

    ::ng-deep .split-buttons__dropdown-button {
      --mat-button-outlined-icon-spacing: 0px;
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
      min-width: 0 !important;
      padding: 0 var(--space) 0 calc(var(--space) * 1.2) !important;
    }

    ::ng-deep .mat-mdc-stroked-button.split-buttons__main-button {
      border-right: none !important;
    }
  `],
  template: `
    <div class="slot-card__left">
      <ng-content select="[risrSlotCardTitle]"></ng-content>
      @if (!customTitle()) {
        @if (link()) {
          <a class="slot-card__title" [routerLink]="link()">{{ title() }}</a>
        } @else {
          <span class="slot-card__title">{{ title() }}</span>
        }
      }
      <ng-content select="[risrSlotCardState]"></ng-content>
      <ng-content select="[risrSlotCardMeta]"></ng-content>
    </div>
    <div class="slot-card__right">
      <ng-content select="[risrSlotCardDate]"></ng-content>
      @if (!customDate() && date()) {
        <span class="slot-card__date">{{ date() | date:'medium' }}</span>
      }
      <ng-content select="[risrSlotCardActions]"></ng-content>
      @if (!customActions() && link()) {
        <div class="slot-card__actions">
          <a mat-stroked-button [routerLink]="link()">{{ actionLabel() }}</a>
        </div>
      }
    </div>
  `
})
export class RisrSlotCard {
  title = input<string>();
  link = input<string | string[]>();
  state = input<RisrState | undefined>();
  actionLabel = input('Open');
  date = input<Date | string>();

  protected readonly color = computed<RisrColor>(() => getSlotColor(this.state()));
  protected customTitle = contentChild(SlotCardTitle);
  protected customDate = contentChild(SlotCardDate);
  protected customActions = contentChild(SlotCardActions);
}
